import type { ServiceOrderState } from '@horology/shared-types';
import { api } from '../../lib/api';

export interface OsItemDTO {
  id: number;
  productId: number;
  description: string | null;
  quantity: string;
  unitPrice: string;
  taxRate: string;
  isLabor: boolean;
  reservedAt: string | null;
  consumedAt: string | null;
  product: { id: number; sku: string; name: string; isService: boolean };
}

export interface OsPhotoDTO {
  id: number;
  slot: number;
  path: string;
  caption: string | null;
}

export interface OsStateLogDTO {
  id: number;
  fromState: ServiceOrderState | null;
  toState: ServiceOrderState;
  comment: string | null;
  createdAt: string;
  changedBy: { id: number; fullName: string };
}

export interface ServiceOrderListItem {
  id: number;
  folio: string;
  state: ServiceOrderState;
  brand: string;
  model: string;
  serial: string | null;
  intakeAt: string;
  promiseAt: string | null;
  estimatedValue: string;
  finalValue: string | null;
  customer: { id: number; firstName: string; lastName: string; idDocument: string; phone: string };
  receivedBy: { id: number; fullName: string };
  technician: { id: number; fullName: string } | null;
}

export interface ServiceOrderDetail extends ServiceOrderListItem {
  signedToken: string;
  reportedDefects: string;
  worksToPerform: string;
  watchKind: string | null;
  caseMaterial: string | null;
  bandMaterial: string | null;
  readyAt: string | null;
  deliveredAt: string | null;
  invoiceId: number | null;
  customer: ServiceOrderListItem['customer'] & { email: string; address: string | null };
  photos: OsPhotoDTO[];
  items: OsItemDTO[];
  stateLogs: OsStateLogDTO[];
  acceptance: {
    id: number;
    acceptedTotal: string;
    ipAddress: string;
    acceptedAt: string;
  } | null;
  invoice: { id: number; number: string; total: string } | null;
}

export interface OsListParams {
  q?: string;
  state?: ServiceOrderState;
  cursor?: number;
  limit?: number;
}

export async function listServiceOrders(
  params: OsListParams = {},
): Promise<{ data: ServiceOrderListItem[]; nextCursor: number | null }> {
  const { data } = await api.get<{ data: ServiceOrderListItem[]; nextCursor: number | null }>(
    '/api/service-orders',
    { params },
  );
  return data;
}

export async function getServiceOrder(id: number): Promise<ServiceOrderDetail> {
  const { data } = await api.get<ServiceOrderDetail>(`/api/service-orders/${id}`);
  return data;
}

export interface CreateOrderBody {
  customerId: number;
  technicianId?: number;
  brand: string;
  model: string;
  serial?: string;
  watchKind?: string;
  caseMaterial?: string;
  bandMaterial?: string;
  reportedDefects: string;
  worksToPerform: string;
  estimatedValue: number;
  promiseAt?: string;
}

export async function createServiceOrder(body: CreateOrderBody): Promise<ServiceOrderDetail> {
  const { data } = await api.post<ServiceOrderDetail>('/api/service-orders', body);
  return data;
}

export async function uploadOrderPhotos(orderId: number, files: File[]): Promise<OsPhotoDTO[]> {
  const fd = new FormData();
  for (const f of files) fd.append('photos', f);
  const { data } = await api.post<OsPhotoDTO[]>(`/api/service-orders/${orderId}/photos`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function deleteOrderPhoto(orderId: number, photoId: number): Promise<void> {
  await api.delete(`/api/service-orders/${orderId}/photos/${photoId}`);
}

export async function transitionOrder(
  orderId: number,
  toState: ServiceOrderState,
  comment?: string,
  warehouseId?: number,
): Promise<ServiceOrderDetail> {
  const { data } = await api.post<ServiceOrderDetail>(
    `/api/service-orders/${orderId}/transition`,
    { toState, comment, warehouseId },
  );
  return data;
}

export async function addOrderItem(
  orderId: number,
  body: {
    productId: number;
    description?: string;
    quantity: number;
    unitPrice: number;
    taxRate: number;
    isLabor: boolean;
  },
): Promise<OsItemDTO> {
  const { data } = await api.post<OsItemDTO>(`/api/service-orders/${orderId}/items`, body);
  return data;
}

export async function deleteOrderItem(orderId: number, itemId: number): Promise<void> {
  await api.delete(`/api/service-orders/${orderId}/items/${itemId}`);
}

export function pdfUrl(
  orderId: number,
  format: 'ticket' | 'letter' | 'half-letter' | 'label' = 'letter',
): string {
  return `/api/service-orders/${orderId}/pdf?format=${format}`;
}
